import { queueDisplaySave } from 'helpers/display_saves';
import ApplicationController from './application_controller';

// Per-goal "Hide graph" toggle on the Goal progress panel. Hidden, the chart
// and its legend give way to a one-line note and the data points below stay;
// shown again, the chart comes back (it renders lazily on first visibility,
// so a graph hidden before it was ever scrolled into view builds on reveal).
// State lives on this element: per goal, per page load on the goal pages. On
// a document's page the element carries a url and goal value, and each toggle
// saves the choice to the document (GoalsV3PanelPresentation), so the page
// comes back as it was left and the print shows the same; a save that does
// not go through is announced (goal-graph-toggle:save-failed) for the page's
// notice, since the page has already changed.
//
// Every toggle announces itself as `goal-graph-toggle:toggled` so the
// data-point disclosure inside the panel can unfold: with the chart gone the
// rows are the only thing left telling the story. The event fires on the
// click, never on connect, so a list the server opened deliberately (a panel
// with no chart to draw) is never closed behind the reader's back.
//
// Targets:
//   graph   the chart canvas wrapper plus its legend row (hidden together)
//   note    the "Graph hidden" line shown in their place
//   button  the toggle (aria-pressed mirrors the hidden state)
//   icon    the eye / eye-slash icon inside the button
//   label   the "Hide graph" / "Show graph" text inside the button
// Values:
//   hidden  the graph starts hidden (a document's saved choice)
//   url     where the choice saves (absent on the goal pages: nothing saves)
//   goal    the goal the choice is for
export default class extends ApplicationController {
  static targets = ['graph', 'note', 'button', 'icon', 'label'];
  static values = { hidden: { type: Boolean, default: false }, url: String, goal: String };

  toggle() {
    this.hiddenValue = !this.hiddenValue;
    this.dispatch('toggled', { detail: { hidden: this.hiddenValue } });
    this.save();
  }

  // One at a time with the page's other display saves (helpers/display_saves), as the panels save.
  save() {
    if (!this.hasUrlValue || !this.urlValue || !this.hasGoalValue) return;
    const url = this.urlValue;
    const body = JSON.stringify({ graphs: { [this.goalValue]: { graph: !this.hiddenValue } } });
    queueDisplaySave(() => {
      const token = document.querySelector('meta[name="csrf-token"]');
      return fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': token ? token.getAttribute('content') : '',
        },
        credentials: 'same-origin',
        body,
      });
    })
      .then((response) => {
        // A redirect is the sign-in page: the session ended and nothing was saved.
        if (!response.ok || response.redirected) throw new Error(`Graph choice not saved (${response.status})`);
      })
      .catch(() => this.dispatch('save-failed'));
  }

  hiddenValueChanged() {
    const hidden = this.hiddenValue;
    this.graphTargets.forEach((element) => {
      element.hidden = hidden;
    });
    this.noteTargets.forEach((element) => {
      element.hidden = !hidden;
    });
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute('aria-pressed', String(hidden));
      this.buttonTarget.title = hidden ? 'Show the graph' : 'Hide the graph';
    }
    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle('bi-eye', hidden);
      this.iconTarget.classList.toggle('bi-eye-slash', !hidden);
    }
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = hidden ? 'Show graph' : 'Hide graph';
    }
  }
}
